/**
 * Scroll reveal: fades content up as it enters the viewport.
 *
 * Performance notes:
 * - One IntersectionObserver for the whole page; no scroll listeners, no layout reads.
 * - Only opacity and translate are animated, so transitions run on the compositor.
 * - Elements are hidden only after the observer reports them off-screen, so anything
 *   visible at first paint (hero, LCP image) is never hidden or delayed.
 * - Content rendered later (section rendering, editor reloads) carries no data-reveal
 *   attribute and stays visible: failure mode is "no animation", never "hidden content".
 *   The one exception is product recommendations, which are watched explicitly: they
 *   arrive after load, just before they scroll into view.
 */

const REVEAL_SELECTOR = [
  '.resource-list__item',
  '.cat-list__card',
  '.fbp-card',
  '.faq-panel__card',
  '.direccion__card',
  '.fcg__media',
  '.fcg__header',
  '.feed-social__header',
  '.feed-social__slider',
  '.text-block',
].join(',');

/** Containers that manage their own motion, or where a reveal would feel wrong. */
const EXCLUDED_ANCESTORS = [
  // Hero slideshows animate their own slides; product carousels are fine to reveal.
  'slideshow-component:not(.resource-list__carousel)',
  'layered-slideshow-component',
  'marquee-component',
  'product-card',
  '.product-card',
  'dialog',
  '[data-no-reveal]',
].join(',');

/** Placeholder cards shown while recommendations load; they get morphed into real cards. */
const SKELETON_SELECTOR = '.product-recommendations__skeleton-item';

const STAGGER_MS = 90;
const MAX_STAGGER_STEPS = 4;

/**
 * @param {Element} element
 * @returns {boolean}
 */
function isRevealCandidate(element) {
  if (element.matches(SKELETON_SELECTOR)) return false;
  if (element.parentElement?.closest(EXCLUDED_ANCESTORS)) return false;
  // Nested targets would animate twice; the outermost one wins.
  return !element.parentElement?.closest(REVEAL_SELECTOR);
}

/**
 * @param {TransitionEvent} event
 */
function cleanUpAfterReveal(event) {
  const element = event.currentTarget;
  if (!(element instanceof HTMLElement) || event.target !== element || event.propertyName !== 'opacity') return;

  // Drop the reveal transition so the element's own hover transitions apply again.
  element.removeEventListener('transitionend', cleanUpAfterReveal);
  delete element.dataset.reveal;
  element.style.removeProperty('--reveal-delay');
}

function initScrollReveal() {
  const main = document.getElementById('MainContent');
  if (!main || !('IntersectionObserver' in window)) return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  /** @type {WeakSet<Element>} */
  const classified = new WeakSet();

  const observer = new IntersectionObserver(
    (entries) => {
      let step = 0;

      for (const entry of entries) {
        const element = /** @type {HTMLElement} */ (entry.target);

        // First report: leave anything already on screen (or above it) untouched.
        if (!classified.has(element)) {
          classified.add(element);

          if (entry.boundingClientRect.top < window.innerHeight) {
            observer.unobserve(element);
          } else {
            element.dataset.reveal = 'pending';
          }
          continue;
        }

        if (!entry.isIntersecting) continue;

        observer.unobserve(element);
        element.style.setProperty('--reveal-delay', `${Math.min(step, MAX_STAGGER_STEPS) * STAGGER_MS}ms`);
        step += 1;
        element.addEventListener('transitionend', cleanUpAfterReveal);
        element.dataset.reveal = 'shown';
      }
    },
    { rootMargin: '0px 0px -8% 0px' }
  );

  for (const element of main.querySelectorAll(REVEAL_SELECTOR)) {
    if (isRevealCandidate(element)) observer.observe(element);
  }

  watchLateContent(main, (element) => {
    // Just inserted, so there's nothing to "keep visible": hide it now (a microtask,
    // before paint) and let the observer's first report fade it in.
    element.dataset.reveal = 'pending';
    classified.add(element);
    observer.observe(element);
  });
}

/**
 * Calls `onElement` for each reveal target inserted once product recommendations load.
 * product-recommendations.js sets data-recommendations-performed right before morphing
 * the real cards in, and the morph runs synchronously, so the cards exist by the time
 * this MutationObserver callback runs.
 * @param {HTMLElement} main
 * @param {(element: HTMLElement) => void} onElement
 */
function watchLateContent(main, onElement) {
  const pendingContainers = new Set(
    main.querySelectorAll('product-recommendations:not([data-recommendations-performed="true"])')
  );
  if (!pendingContainers.size) return;

  const mutationObserver = new MutationObserver((mutations) => {
    for (const { target } of mutations) {
      if (!(target instanceof HTMLElement) || target.dataset.recommendationsPerformed !== 'true') continue;
      if (!pendingContainers.delete(target)) continue;
      if (!pendingContainers.size) mutationObserver.disconnect();

      for (const element of target.querySelectorAll(REVEAL_SELECTOR)) {
        if (element instanceof HTMLElement && !element.dataset.reveal && isRevealCandidate(element)) onElement(element);
      }
    }
  });

  for (const container of pendingContainers) {
    mutationObserver.observe(container, { attributes: true, attributeFilter: ['data-recommendations-performed'] });
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initScrollReveal, { once: true });
} else {
  initScrollReveal();
}
